/**
 * Google A2UI v0.8 server→client：單則訊息允許的頂層信封鍵（每則物件僅含其一）。
 * @type {ReadonlySet<string>}
 */
export const A2UI_V08_ENVELOPE_KEYS = new Set([
  'surfaceUpdate',
  'dataModelUpdate',
  'beginRendering',
  'deleteSurface',
]);

function isNonEmptyString(s) {
  return typeof s === 'string' && s.trim().length > 0;
}

function trimString(s) {
  return typeof s === 'string' ? s.trim() : '';
}

/**
 * 自巢狀 API／訊息物件擷取待解析之 `content.text` 同級字串。
 * @param {unknown} obj
 * @returns {string | null}
 */
export function extractA2uiTextFromObject(obj) {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return null;
  const candidates = [
    obj?.data?.data?.content?.text,
    obj?.data?.content?.text,
    obj?.content?.text,
    obj?.text,
  ];
  for (const c of candidates) {
    if (typeof c === 'string') {
      const t = trimString(c);
      if (t !== '') return t;
    }
  }
  return null;
}

/**
 * @param {string} type
 * @param {unknown} body
 * @returns {boolean}
 */
function validateEnvelopeBody(type, body) {
  if (!body || typeof body !== 'object' || Array.isArray(body)) return false;
  switch (type) {
    case 'beginRendering':
      return isNonEmptyString(body.surfaceId) && isNonEmptyString(body.root);
    case 'surfaceUpdate':
      return isNonEmptyString(body.surfaceId) && Array.isArray(body.components);
    case 'dataModelUpdate':
      return isNonEmptyString(body.surfaceId) && typeof body.path === 'string';
    case 'deleteSurface':
      return isNonEmptyString(body.surfaceId);
    default:
      return false;
  }
}

/**
 * @param {unknown} value
 * @returns {boolean}
 */
function validateMessagesArray(value) {
  if (!Array.isArray(value) || value.length === 0) return false;
  for (const el of value) {
    if (!el || typeof el !== 'object' || Array.isArray(el)) return false;
    const keys = Object.keys(el);
    if (keys.length !== 1) return false;
    const k = keys[0];
    if (!A2UI_V08_ENVELOPE_KEYS.has(k)) return false;
    if (!validateEnvelopeBody(k, el[k])) return false;
  }
  return true;
}

/**
 * 由字串解析為 v0.8 訊息陣列；失敗則回傳 null。
 * @param {string} text
 * @returns {unknown[] | null}
 */
function tryParseMessagesString(text) {
  try {
    const v = JSON.parse(text);
    return Array.isArray(v) ? v : null;
  } catch {
    return null;
  }
}

/**
 * 判斷輸入是否代表機器人回覆中的 A2UI v0.8 訊息序列（通常為 `content.text` 內之 JSON 陣列字串）。
 * 任何無法辨識或結構不符時回傳 `false`，不拋錯。
 *
 * @param {unknown} input 完整 API JSON、訊息物件、或內層／外層 JSON 字串
 * @returns {boolean}
 */
export function isA2uiV08BotReplyPayload(input) {
  try {
    if (input === null || input === undefined) return false;

    if (typeof input === 'string') {
      const s = trimString(input);
      if (s === '') return false;
      let outer;
      try {
        outer = JSON.parse(s);
      } catch {
        return false;
      }
      if (Array.isArray(outer)) return validateMessagesArray(outer);
      if (outer && typeof outer === 'object') {
        const innerText = extractA2uiTextFromObject(outer);
        if (!innerText) return false;
        const arr = tryParseMessagesString(innerText);
        return arr !== null && validateMessagesArray(arr);
      }
      return false;
    }

    if (typeof input === 'object') {
      if (Array.isArray(input)) return validateMessagesArray(input);
      const innerText = extractA2uiTextFromObject(input);
      if (!innerText) return false;
      const arr = tryParseMessagesString(innerText);
      return arr !== null && validateMessagesArray(arr);
    }

    return false;
  } catch {
    return false;
  }
}

// DevTools：`window.__isA2uiV08BotReplyPayload(payload)`；入口須 import 本檔
if (typeof window !== 'undefined') {
  window.__isA2uiV08BotReplyPayload = isA2uiV08BotReplyPayload;
}
